import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ColumnDef } from '@tanstack/react-table';
import { ArchiveRestore, BarChart3, Eye, MoreHorizontal, Trash2 } from 'lucide-react';
import apiService from '../services/api.service';
import { Event } from '../types';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { PageHeader, Toolbar } from '../components/ui/page-header';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { DataTable } from '../components/data-table/DataTable';
import { BulkAction, BulkActionBar } from '../components/data-table/BulkActionBar';
import { SearchField } from '../components/data-table/SearchField';

// Archives (refonte 2026-09-27) : les événements archivés dans la grammaire de
// la page Équipes. Deux gestes : restaurer (réversible, sans confirmation) et
// supprimer définitivement (confirmé, il emporte équipes et résultats).

const formatError = (err: any, fallback: string): string => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((e: any) => e.msg || e.message || JSON.stringify(e)).join(', ');
  return fallback;
};

const Archives: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const eventsQuery = useQuery({
    queryKey: ['events', 'archived'],
    queryFn: async () => ((await apiService.getEvents()).data || []).filter((e: Event) => e.status === 'archived') as Event[],
  });
  const events = useMemo(() => eventsQuery.data ?? [], [eventsQuery.data]);
  const refresh = () => { qc.invalidateQueries({ queryKey: ['events'] }); };

  const formatDateRange = (e: Event): string => {
    const start = e.date_start || e.date;
    if (!start) return t('archives.dateNotDefined');
    const s = new Date(start).toLocaleDateString();
    if (e.date_end && e.date_end !== start) return `${s} → ${new Date(e.date_end).toLocaleDateString()}`;
    return s;
  };
  const typeLabel = (e: Event) => (e.event_type === 'crossfit' ? t('events.crossfit') : t('events.course'));
  const resultsPath = (e: Event) => (e.event_type === 'crossfit' ? `/admin/events/${e.id}/crossfit-results` : `/admin/events/${e.id}/race-results`);

  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return events.filter((e) => !q || [e.name, e.location, e.location_city, typeLabel(e)].filter(Boolean).join(' ').toLowerCase().includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, query]);

  const [selection, setSelection] = useState<Record<string, boolean>>({});
  const selected = events.filter((e) => selection[e.id]);
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState<Event[]>([]);

  const restore = async (targets: Event[]) => {
    setBusy(true);
    const failed: string[] = [];
    for (const e of targets) { try { await apiService.restoreEvent(e.id); } catch { failed.push(e.name); } }
    setBusy(false); setSelection({}); refresh();
    if (failed.length) toast.error(t('archives.list.failed', { names: failed.join(', ') }));
    else toast.success(t('archives.list.restored', { count: targets.length }));
  };
  const remove = async () => {
    setBusy(true);
    const failed: string[] = [];
    for (const e of toDelete) { try { await apiService.deleteEvent(e.id); } catch (err) { failed.push(`${e.name} (${formatError(err, t('common.unknownError'))})`); } }
    setBusy(false); setSelection({}); setToDelete([]); refresh();
    if (failed.length) toast.error(t('archives.list.failed', { names: failed.join(', ') }));
    else toast.success(t('archives.list.deleted', { count: toDelete.length }));
  };

  const columns = useMemo<ColumnDef<Event, any>[]>(() => [
    {
      id: 'name', accessorFn: (e) => e.name, header: () => t('archives.table.name'),
      sortingFn: (a, b) => a.original.name.localeCompare(b.original.name, undefined, { sensitivity: 'base' }),
      cell: ({ row }) => <span className="font-semibold text-foreground">{row.original.name}</span>,
    },
    { id: 'type', accessorFn: (e) => typeLabel(e), header: () => t('archives.table.type'), cell: ({ getValue }) => <span className="text-muted-foreground">{getValue()}</span> },
    { id: 'date', accessorFn: (e) => e.date_start || e.date || '', header: () => t('archives.table.date'), cell: ({ row }) => <span className="whitespace-nowrap text-muted-foreground">{formatDateRange(row.original)}</span> },
    { id: 'location', accessorFn: (e) => e.location_city || e.location || '', header: () => t('archives.table.location'), cell: ({ getValue }) => <span className="text-muted-foreground">{getValue() || '—'}</span> },
    {
      id: 'actions', enableSorting: false, header: () => <span className="sr-only">{t('common.actions')}</span>,
      cell: ({ row }) => (
        <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" aria-label={t('archives.list.rowActions', { name: row.original.name })}><MoreHorizontal size={16} /></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate(`/events/${row.original.id}`)}><Eye size={16} className="mr-2" />{t('archives.actions.viewDetails')}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(resultsPath(row.original))}><BarChart3 size={16} className="mr-2" />{t('archives.actions.viewResults')}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => restore([row.original])}><ArchiveRestore size={16} className="mr-2" />{t('archives.actions.restore')}</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => setToDelete([row.original])}><Trash2 size={16} className="mr-2" />{t('archives.actions.deleteForever')}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  return (
    <div className="space-y-5 p-4 md:p-8">
      <PageHeader title={t('archives.title')} meta={t('archives.list.count', { count: events.length })} />

      <Toolbar>
        <SearchField value={query} onChange={setQuery} placeholder={t('archives.searchPlaceholder')} />
        {query && <span className="text-xs text-muted-foreground">{t('teams.filteredCount', { shown: filtered.length, total: events.length })}</span>}
      </Toolbar>

      {eventsQuery.isError && (
        <Alert variant="destructive"><AlertDescription className="flex items-center justify-between gap-4">{t('archives.loadError')}<Button variant="outline" size="sm" onClick={() => eventsQuery.refetch()}>{t('common.retry')}</Button></AlertDescription></Alert>
      )}

      <DataTable
        ariaLabel={t('archives.title')}
        columns={columns}
        data={filtered}
        getRowId={(e) => e.id}
        loading={eventsQuery.isLoading}
        selectable
        rowSelection={selection}
        onRowSelectionChange={setSelection}
        onRowClick={(e) => navigate(`/events/${e.id}`)}
        initialSorting={[{ id: 'date', desc: true }]}
        empty={events.length === 0 ? (
          <div className="space-y-1"><p className="text-base text-foreground">{t('archives.noArchives')}</p><p>{t('archives.archivesWillAppear')}</p></div>
        ) : (
          <div className="space-y-1"><p className="text-base text-foreground">{t('archives.noMatch')}</p><p>{t('archives.tryOtherTerms')}</p></div>
        )}
        renderCard={(e, { selected: sel, toggle }) => (
          <div className="flex items-start gap-3 py-3">
            <input type="checkbox" checked={sel} onChange={toggle} onClick={(ev) => ev.stopPropagation()} aria-label={t('dataTable.selectRow')} className="mt-1 h-4 w-4 accent-white" />
            <div className="min-w-0 flex-1">
              <div className="truncate font-semibold">{e.name}</div>
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span>{typeLabel(e)}</span><span>{formatDateRange(e)}</span>{(e.location_city || e.location) && <span>{e.location_city || e.location}</span>}
              </div>
            </div>
          </div>
        )}
      />

      <BulkActionBar count={selected.length} onClear={() => setSelection({})}>
        <BulkAction disabled={busy} onClick={() => restore(selected)}><ArchiveRestore />{t('archives.actions.restore')}</BulkAction>
        <BulkAction disabled={busy} className="text-destructive" onClick={() => setToDelete(selected)}><Trash2 />{t('archives.actions.deleteForever')}</BulkAction>
      </BulkActionBar>

      <ConfirmDialog
        open={toDelete.length > 0}
        onOpenChange={(o) => { if (!o && !busy) setToDelete([]); }}
        title={toDelete.length === 1 ? t('archives.deleteConfirmTitle', { name: toDelete[0].name }) : t('archives.list.deleteManyTitle', { count: toDelete.length })}
        message={t('archives.deleteConfirmMessage')}
        confirmLabel={t('archives.actions.deleteForever')}
        destructive
        loading={busy}
        onConfirm={remove}
      />
    </div>
  );
};

export default Archives;
